import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { criarFiltroMarca } from '../utils/marca'

const RECORDS_FIELD = 'records'
const TRADEMARK_FIELD = 'marcaConfiguracao'

/**
 * Hook responsável por filtrar os dados de KPI pelas marcas selecionadas.
 *
 * Aplica o filtro em arrays cujos itens possuam a propriedade
 * `marcaConfiguracao`, incluindo estruturas aninhadas através da propriedade
 * `records`. Itens sem a propriedade não são alterados; itens com marca vazia
 * entram apenas quando "Sem marca" está selecionada.
 *
 * @param {Object} kpi Dados de KPI.
 * @returns {Object} Dados filtrados conforme as marcas selecionadas.
 */
export default function useTrademarkFilter(kpi) {
    const selectedTrademarks = useSelector((state) => state.trademark.selecteds)

    const filteredKpi = useMemo(() => {
        if (!kpi || !selectedTrademarks.length) {
            return kpi
        }

        const kpiCopy = { ...kpi }

        // filtra apenas se tiver a propriedade marcaConfiguracao
        const filterTrademark = criarFiltroMarca(selectedTrademarks)

        // trata listas com itens que tem records dentro
        function filterRecordsByTrademark(list) {
            return list.map(item => ({
                ...item,
                [RECORDS_FIELD]: item[RECORDS_FIELD].filter(record => filterTrademark(record))
            })).filter(item => item[RECORDS_FIELD].length > 0)
        }

        Object.keys(kpiCopy).forEach(key => {
            const value = kpiCopy[key]
            if (!Array.isArray(value)) {
                return
            }

            // caso o array não tenha records será apenas filtrado o marcaConfiguracao
            const hasRecords = value[0]?.hasOwnProperty(RECORDS_FIELD)
            if (!hasRecords) {
                if (!value[0]?.hasOwnProperty(TRADEMARK_FIELD)) {
                    return
                }

                kpiCopy[key] = value.filter(item => filterTrademark(item))
                return
            }

            // irá criar uma nova lista com os records filtrados
            // caso não tenha nenhum record o objeto inteiro é omitido
            kpiCopy[key] = filterRecordsByTrademark(value)
        })

        return kpiCopy
    }, [kpi, selectedTrademarks])

    return filteredKpi
}